import {Component, HostListener, isDevMode, OnDestroy, OnInit} from '@angular/core';
import {Router} from '@angular/router';
import {ControlDesk} from '../../models/turns-models/control-desk.model';
import {LocationDesk} from '../../models/turns-models/location-desk.model';
import {TurnsService} from '../../services/turns-services/turns-service/turns.service';
import {UserStorageService} from '../../services/user-storage-service/user-storage.service';
import {map, tap} from 'rxjs/internal/operators';
import {Subscription} from 'rxjs';
import {CommunicationService} from '../../services/communication-service/communication.service';
import {MaintenanceService} from '../../services/maintenance-service/maintenance.service';
import {PendingTurn} from '../../models/turns-models/pending-turn.model';
import {APP_CONFIG} from '../../config';
import {MatDialog} from '@angular/material/dialog';
import {AddNotesDialogComponent} from '../add-notes-dialog/add-notes-dialog.component';
import {NativeAudioService} from '../../services/native-audio.service';


@Component({
  selector: 'app-control-screen',
  templateUrl: './control-screen.component.html',
  styleUrls: ['./control-screen.component.scss']
})
export class ControlScreenComponent implements OnInit, OnDestroy {

  version: string = APP_CONFIG.appVersion;

  public openMenuTurnId: string | null = null;
  public openTransferMenu = false;
  public openInformationMenu = false;

  transferOptions = [
    {icon: 'group_add', value: 'SERVICE_QUEUE'},
    {icon: 'person', value: 'SERVICE_NEXT'},
    {icon: 'desktop_windows', value: 'LOCATION'}
  ];
  selectedTransferMode: string | null = null;

  informationOptions = [
    {icon: 'edit_note', value: 'ADD_NOTES'},
    {icon: 'build', value: 'ADD_SERVICE'},
  ];

  allServices: ControlDesk[] = [];
  allLocations: LocationDesk[] = [];

  pendingTurns: PendingTurn[] = [];
  private timerInterval: any;

  selectedDesk: ControlDesk;
  selectedLocation: LocationDesk;
  selectedLocationClosed = false;

  companyImage: string;

  currentTurnInternalId: string;
  currentTurn: string;
  currentCustomerName: string;
  currentDeskName: string;
  currentWaitingAndAttendingTurns;
  currentWaitingTime: number;
  currentServiceTime = 0;
  currentStatus: string | null = null;

  listenDeskAdvanceObjectSubscription: Subscription;
  listenUpdateDesksSubscription: Subscription;
  listenTurnTakenSubscription: Subscription;

  isFlipped = false;

  public isMobileView = false;
  public isSidePanelVisibleOnMobile = false;

  @HostListener('window:resize', ['$event'])
  onResize(event?) {
    this.isMobileView = window.innerWidth < 992;
    if (!this.isMobileView) {
      this.isSidePanelVisibleOnMobile = false;
    }
  }

  constructor(
    private maintenanceService: MaintenanceService,
    private communicationService: CommunicationService,
    private turnsService: TurnsService,
    private userStorageService: UserStorageService,
    private nativeAudioService: NativeAudioService,
    private router: Router,
    public dialog: MatDialog
  ) {
    this.selectedDesk = this.userStorageService.getSelectedDesk();
    this.selectedLocation = this.userStorageService.getSelectedLocationDesk();
    this.checkDeskLocationStatus();
    if (this.selectedDesk) {
      this.updateCurrentDesk();
    } else if (this.selectedLocation) {
      this.updateCurrentLocation();
    }
  }

  ngOnInit(): void {
    this.onResize();
    this.maintenanceService.isRegistered().pipe(
      map(registered => {
        if (!registered['isRegistered']) {
          this.router.navigate(['/']);
        } else {
          this.userStorageService.setContext(registered['context']);
          this.turnsService.getCompanyInfo(this.userStorageService.getCompanyInternalId()).pipe(
            tap(o => this.companyImage = o['imageXl'])
          ).subscribe();
          this.selectedDesk = this.userStorageService.getSelectedDesk();
          this.selectedLocation = this.userStorageService.getSelectedLocationDesk();
          if (this.selectedDesk === null && this.selectedLocation === null) {
            this.router.navigate(['/admin']);
          } else {
            if (this.selectedDesk) {
              this.updateCurrentDesk();
            } else if (this.selectedLocation) {
              this.updateCurrentLocation();
            }
          }
          this.communicationService.listenCommunications();
        }})
    ).subscribe();

    this.listenDeskAdvanceObjectSubscription = this.turnsService.listenDeskAdvanceObject().subscribe(
      (res) => {
        const data = JSON.parse(res);
        if (isDevMode()) {
          console.log('onListenDeskAdvanceObject');
          console.log(JSON.stringify(data));
        }
        if ((this.selectedDesk !== null && this.selectedDesk.internalId === data.deskInternalId) ||
          (this.selectedLocation !== null && this.selectedLocation.internalId === data.location.internalId)
        ) {
          if (data.turnStatus !== 'ATTENDING') {
            this.currentTurn = null;
            this.currentTurnInternalId = null;
            this.currentCustomerName = null;
          } else {
            this.currentTurnInternalId = data.turnInternalId;
            this.currentTurn = data.currentTurn;
            this.currentCustomerName = data.customerName;
            if (this.selectedDesk && this.selectedDesk.internalId === data.deskInternalId) {
              this.currentDeskName = null;
              this.currentWaitingAndAttendingTurns = data.peopleWaiting;
            } else if (this.selectedLocation) {
              const location = data.location;
              this.selectedLocation.name = location.name;
              this.selectedLocation.numeration = location.numeration;
              this.selectedLocation.status = location.status;
              this.selectedLocation.turnAlias = data.currentTurn;
              this.selectedLocation.turnDeskName = data.deskName;
              this.currentDeskName = data.deskName;
              this.currentWaitingAndAttendingTurns = location.sumPeopleWaitingAtItsDesks;
            }
            this.currentWaitingTime = data.secondsInWaiting;
            this.currentServiceTime = data.secondsInAttending;
          }
          this.currentStatus = data.turnStatus;
        } else if (data.location.internalId === null && this.currentTurnInternalId === data.turnInternalId) {
          this.currentTurn = null;
          this.currentTurnInternalId = null;
        }
        this.getPendingTurns();
      }
    );

    this.listenUpdateDesksSubscription = this.turnsService.listenUpdateDesks().subscribe(
      (res) => {
        this.getControlDesks();
      }
    );

    this.listenTurnTakenSubscription = this.turnsService.listenTurnTaken().subscribe(
      (res) => {
        this.nativeAudioService.playNewTurnSound();
        if (this.selectedDesk) {
          this.updateCurrentDesk();
        } else if (this.selectedLocation) {
          this.updateCurrentLocation();
        }
        this.getPendingTurns();
      }
    );
    this.getPendingTurns();
    this.startTimers();
  }

  // playAudio() {
  //   if (this.userStorageService.isSoundEnabled()) {
  //     if (window.Android && typeof window.Android.playTurnSound === 'function') {
  //       window.Android.playTurnSound();
  //     } else {
  //       // Fallback para el navegador normal
  //       this.turnSound.play();
  //     }
  //     const audio = new Audio();
  //     audio.src = '../../../assets/audios/turn_arrived.mp3';
  //     audio.load();
  //     audio.play();
  //   }
  // }

  toggleMenu(turn: any, event: MouseEvent): void {
    event.stopPropagation();
    if (this.openMenuTurnId === turn.turn) {
      this.openMenuTurnId = null;
    } else {
      this.openMenuTurnId = turn.turn;
    }
  }

  toggleTransferMenu(event: MouseEvent): void {
    event.stopPropagation();
    this.openInformationMenu = false;
    this.openTransferMenu = !this.openTransferMenu;
  }

  toggleInformationMenu(event: MouseEvent): void {
    event.stopPropagation();
    this.openTransferMenu = false;
    this.openInformationMenu = !this.openInformationMenu;
  }

  selectInformationOption(option: { icon: string, value: string }): void {
    this.openInformationMenu = false;
    switch (option.value) {
      case 'ADD_NOTES':
        this.openAddNotesDialog();
        break;
      case 'ADD_SERVICE':
        console.log('Add Service selected');
        break;
    }
  }

  openAddNotesDialog() {
    const addNotesDialogRef = this.dialog.open(
      AddNotesDialogComponent, {
        width: '500px',
        data: {
          notes: this.currentCustomerName,
          turnInternalId: this.currentTurnInternalId
        }
      }
    );
    addNotesDialogRef.afterClosed().subscribe((success: boolean) => {
      if (success) {
        if (this.selectedDesk) {
          this.updateCurrentDesk();
        } else if (this.selectedLocation) {
          this.updateCurrentLocation();
        }
      }
    });
  }

  transferTo(transfer): void {
    this.selectedTransferMode = transfer.value;
    this.openTransferMenu = false;
    if (transfer.value === 'LOCATION') {
      this.turnsService.getDeskLocations().subscribe(
        (res) => {
          this.allLocations = res;
          this.isFlipped = true;
        },
        (error) => {
          if (this.allLocations.length > 0) {
            this.isFlipped = true;
          }
        }
      );
    } else {
      this.turnsService.getControlAllDesks().subscribe(
        (res) => {
          this.allServices = res;
          this.isFlipped = true;
        },
        (error) => {
          if (this.allServices.length > 0) {
            this.isFlipped = true;
          }
        }
      );
    }
  }

  callSelectedTurn(turn) {
    if (this.selectedLocation) {
      this.turnsService.callNextDeskLocationTurn(this.selectedLocation.internalId, turn.turnListinternalId).subscribe();
    }
    this.showMainPanelOnMobile();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const targetElement = event.target as HTMLElement;
    if (this.openMenuTurnId && !targetElement.closest('.more-options')) {
      this.openMenuTurnId = null;
    }
    if (this.openTransferMenu && !targetElement.closest('.transfer-button-wrapper')) {
      this.openTransferMenu = false;
    }
    if (this.openInformationMenu && !targetElement.closest('.information-button-wrapper')) {
      this.openInformationMenu = false;
    }
  }

  getPendingTurns() {
    this.turnsService.getTurnsWaiting(
      this.selectedDesk ? this.selectedDesk.internalId : null,
      this.selectedLocation ? this.selectedLocation.internalId : null
    ).subscribe(
      (res) => {
        this.pendingTurns = res;
      },
      (error) => {
        console.dir(error);
      }
    );
  }

  ngOnDestroy(): void {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
    if (this.listenDeskAdvanceObjectSubscription) { this.listenDeskAdvanceObjectSubscription.unsubscribe(); }
    if (this.listenUpdateDesksSubscription) { this.listenUpdateDesksSubscription.unsubscribe(); }
    if (this.listenTurnTakenSubscription) { this.listenTurnTakenSubscription.unsubscribe(); }
  }

  private startTimers(): void {
    this.timerInterval = setInterval(() => {
      if (this.currentTurn && this.currentStatus === 'ATTENDING') {
        this.currentServiceTime++;
      }
      this.pendingTurns.forEach(pendingTurn => {
        pendingTurn.waitingTimeSeconds++;
      });
    }, 1000);
  }

  callNextTurn(event: Event) {
    this.checkTouchEvent(event);
    if (this.selectedLocation) {
      this.turnsService.callNextDeskLocation(this.selectedLocation.internalId).subscribe();
    } else {
      this.turnsService.pushTurn(this.selectedDesk.internalId).subscribe();
    }
    this.currentServiceTime = 0;
  }

  endTurn(event: Event): void {
    this.checkTouchEvent(event);
    this.turnsService.finishAttendance(
      this.selectedDesk ? this.selectedDesk.internalId : null,
      this.selectedLocation ? this.selectedLocation.internalId : null
    ).subscribe();
    this.currentServiceTime = 0;
  }

  addService(): void { console.log('Add service clicked.'); }

  checkTouchEvent(event: Event) {
    if (event.type === 'touchend') {
      event.preventDefault();
    }
  }

  goToAdmin() {
    this.router.navigate(['/admin']);
  }

  openLocation() {
    if (this.selectedLocation) {
      this.turnsService.callOpenDeskLocation(this.selectedLocation.internalId).subscribe(
        (res) => {
          if (res.success) {
            this.updateCurrentLocation();
          }
        },
        (error) => {
          console.dir(error);
        }
      );
    }
  }

  closeLocation() {
    if (this.selectedLocation) {
      this.turnsService.callCloseDeskLocation(this.selectedLocation.internalId).subscribe(
        (res) => {
          if (res.success) {
            this.updateCurrentLocation();
          }
        },
        (error) => {
          console.dir(error);
        }
      );
    }
  }

  private getControlDesks() {
    this.turnsService.getControlDesks().subscribe(
      (res) => {
        if (this.selectedDesk) {
          const updatedDesk = res.filter(desk => desk.internalId === this.selectedDesk.internalId)[0];
          this.userStorageService.setSelectedDesk(updatedDesk);
          this.selectedDesk = updatedDesk;
          this.currentTurn = (this.selectedDesk.turnPrefix ? this.selectedDesk.turnPrefix : '') + (
            this.selectedDesk.lastCalledTurnAlias ?
              this.selectedDesk.lastCalledTurnAlias :
              this.selectedDesk.lastCalledTurnNumber
          );
          this.currentDeskName = null;
          this.currentWaitingAndAttendingTurns = this.selectedDesk.waitingAndAttendingTurns;
        }
      },
      (error) => {
        console.dir(error);
      }
    );
  }

  private updateCurrentLocation() {
    if (this.selectedLocation) {
      this.turnsService.updateCurrentLocation(this.selectedLocation.internalId).subscribe(
        (res) => {
          if (res.turnStatus !== 'ATTENDING') {
            this.currentTurn = null;
            this.currentTurnInternalId = null;
            this.currentCustomerName = null;
          } else {
            this.selectedLocation.name = res.name;
            this.selectedLocation.numeration = res.numeration;
            this.selectedLocation.turnAlias = res.turnAlias;
            this.selectedLocation.turnDeskName = res.turnDeskName;
            this.currentTurnInternalId = res.turnInternalId;
            this.currentTurn = this.selectedLocation.turnAlias;
            this.currentCustomerName = res.customerName;
            this.currentDeskName = this.selectedLocation.turnDeskName;
            this.currentWaitingAndAttendingTurns = res.sumPeopleWaitingAtItsDesks;
            this.currentWaitingTime = res.secondsInWaiting;
            this.currentServiceTime = res.secondsInAttending;
            this.currentStatus = res.turnStatus;
          }
          this.selectedLocation.status = res.status;
          this.checkDeskLocationStatus();
        },
        (error) => {
          console.dir(error);
        }
      );
    }
  }

  private updateCurrentDesk() {
    if (this.selectedDesk) {
      this.turnsService.updateCurrentDesk(this.selectedDesk.internalId).subscribe(
        (res) => {
          if (res.turnStatus !== 'ATTENDING') {
            this.currentTurn = null;
            this.currentTurnInternalId = null;
          } else {
            this.selectedDesk.turnPrefix = res.turnPrefix;
            this.selectedDesk.lastCalledTurnAlias = res.turnAlias;
            this.selectedDesk.lastCalledTurnNumber = res.lastCalledTurnNumber;
            this.currentTurnInternalId = res.turnInternalId;
            this.currentTurn = res.turnAlias;
            this.currentCustomerName = res.customerName;
            this.currentDeskName = null;
            this.currentWaitingAndAttendingTurns = res.peopleWaiting;
            this.currentWaitingTime = res.secondsInWaiting;
            this.currentServiceTime = res.secondsInAttending;
            this.currentStatus = res.turnStatus;
            this.userStorageService.setSelectedDesk(this.selectedDesk);
          }
        },
        (error) => {
          console.dir(error);
        }
      );
    }
  }

  private checkDeskLocationStatus() {
    if (this.selectedLocation) {
      this.selectedLocationClosed = this.selectedLocation.status === 'CLOSED';
    }
  }

  recall(): void {
    if (this.selectedLocation) {
      this.turnsService.callbackLastLocationTurn(this.selectedLocation.internalId).subscribe();
    } else {
      this.turnsService.callBackLastTurn(this.selectedDesk.internalId).subscribe();
    }
  }

  requeue() {
    if (this.selectedLocation) {
      this.turnsService.requeueTurn(this.selectedLocation.internalId).subscribe();
    }
  }

  clientAbsent(): void {
    if (this.selectedLocation) {
      this.turnsService.callNextDueToAbsence(this.selectedLocation.internalId).subscribe();
    } else {
      this.turnsService.pushTurnDueToAbsence(this.selectedDesk.internalId).subscribe();
    }
  }

  clientWithoutTicket(): void {
    this.selectedTransferMode = null;
    this.turnsService.getControlAllDesks().subscribe(
      (res) => {
        if (this.selectedDesk) {
          this.allServices = res.filter( (d: ControlDesk) => d.type === 'NORMAL');
        } else {
          this.allServices = res.filter( (d: ControlDesk) => d.type === 'MULTIPLE_ATTENDING');
        }
        this.isFlipped = true;
      },
      (error) => {
        if (this.allServices.length > 0) {
          this.isFlipped = true;
        }
      }
    );
  }

  attendInService(turnDeskInternalId): void {
    if (this.selectedTransferMode === null) {
      const locationId = this.selectedLocation ? this.selectedLocation.internalId : null;
      this.turnsService.attendWithoutTurn(
        turnDeskInternalId,
        locationId
      ).subscribe(
        (res) => { },
        (error) => {
          console.dir(error);
        }
      );
    } else {
      this.turnsService.transfer(
        this.currentTurnInternalId,
        turnDeskInternalId,
        null,
        this.selectedTransferMode
      ).subscribe(
        (res) => { },
        (error) => {
          console.dir(error);
        }
      );
    }
    this.isFlipped = false;
  }

  attendInLocation(locationInternalId): void {
    if (this.selectedTransferMode !== null) {
      this.turnsService.transfer(
        this.currentTurnInternalId,
        null,
        locationInternalId,
        this.selectedTransferMode
      ).subscribe(
        (res) => { },
        (error) => {
          console.dir(error);
        }
      );
    }
    this.isFlipped = false;
  }

  flipBack(): void {
    this.isFlipped = false;
    this.selectedTransferMode = null;
  }

  toggleMobilePanelView(): void {
    if (this.isMobileView) {
      this.isSidePanelVisibleOnMobile = !this.isSidePanelVisibleOnMobile;
    }
  }

  showMainPanelOnMobile(): void {
    if (this.isMobileView) {
      this.isSidePanelVisibleOnMobile = false;
    }
  }
}
